import { useState, useEffect, useRef } from "react";

/* ─── INTERSECTION OBSERVER HOOK ─── */
const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
};

/* ─── ANIMATED SECTION ─── */
const Reveal = ({ children, delay = 0, dir = "up" }) => {
  const [ref, inView] = useInView();
  const transforms = { up: "translateY(48px)", down: "translateY(-48px)", left: "translateX(-48px)", right: "translateX(48px)" };
  return (
    <div ref={ref} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? "translate(0)" : transforms[dir],
      transition: `opacity 0.75s cubic-bezier(.16,1,.3,1) ${delay}s, transform 0.75s cubic-bezier(.16,1,.3,1) ${delay}s`,
    }}>{children}</div>
  );
};

/* ─── TYPEWRITER ─── */
const TypeWriter = ({ words }) => {
  const [text, setText] = useState("");
  const [wi, setWi] = useState(0);
  const [ci, setCi] = useState(0);
  const [del, setDel] = useState(false);
  useEffect(() => {
    const word = words[wi];
    const timer = setTimeout(() => {
      if (!del) {
        setText(word.slice(0, ci + 1));
        if (ci + 1 === word.length) setTimeout(() => setDel(true), 1800);
        else setCi(ci + 1);
      } else {
        setText(word.slice(0, ci - 1));
        if (ci - 1 === 0) { setDel(false); setWi((wi + 1) % words.length); setCi(0); }
        else setCi(ci - 1);
      }
    }, del ? 45 : 95);
    return () => clearTimeout(timer);
  }, [text, ci, del, wi, words]);
  return (
    <span style={{ color: "#E84A1A", fontWeight: 700 }}>
      {text}
      <span style={{ borderRight: "2.5px solid #E84A1A", animation: "blink 1s infinite", marginLeft: 1 }}>&nbsp;</span>
    </span>
  );
};

/* ─── SKILL BAR ─── */
const SkillBar = ({ label, pct, delay }) => {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} style={{ marginBottom: 22 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 13, fontWeight: 600, color: "#1e3a5f" }}>
        <span>{label}</span>
        <span style={{ color: "#E84A1A", fontWeight: 800 }}>{pct}%</span>
      </div>
      <div style={{ height: 8, background: "#dce8f8", borderRadius: 99, overflow: "hidden" }}>
        <div style={{
          height: "100%", borderRadius: 99,
          background: "linear-gradient(90deg, #0057B8 0%, #E84A1A 100%)",
          width: inView ? `${pct}%` : "0%",
          transition: `width 1.4s cubic-bezier(.16,1,.3,1) ${delay}s`,
          boxShadow: "0 2px 12px rgba(0,87,184,0.25)"
        }} />
      </div>
    </div>
  );
};

/* ─── FLOATING PARTICLES ─── */
const Particles = () => {
  const particles = Array.from({ length: 18 }, (_, i) => ({
    id: i, size: 4 + (i * 3.7) % 8,
    x: (i * 17.3) % 100, y: (i * 23.7) % 100,
    dur: 6 + (i * 1.3) % 8, delay: (i * 0.7) % 5,
    opacity: 0.1 + (i * 0.013) % 0.2,
    color: i % 3 === 0 ? "#E84A1A" : i % 3 === 1 ? "#0057B8" : "#ffffff",
  }));
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
      {particles.map(p => (
        <div key={p.id} style={{
          position: "absolute", left: `${p.x}%`, top: `${p.y}%`,
          width: p.size, height: p.size, borderRadius: "50%",
          background: p.color, opacity: p.opacity,
          animation: `floatP ${p.dur}s ease-in-out ${p.delay}s infinite alternate`,
        }} />
      ))}
    </div>
  );
};

/* ─── DATA ─── */
const skills = [
  { label: "React.js / Next.js", pct: 60 },
  { label: "Node.js / Express", pct: 60 },
  { label: "React Native", pct: 40 },
  { label: "MongoDB", pct: 70 },
  { label: "PHP", pct: 90 },
  { label: "MySQL", pct: 100 },
];

const competencies = [
  { icon: "⚛️", title: "Front-End", desc: "React.js, HTML5, CSS3, Tailwind, Bootstrap — pixel-perfect, performant UIs", color: "#eef4ff", border: "#c2d8f8" },
  { icon: "🔧", title: "Back-End", desc: "Node.js, Express.js, REST APIs, Auth flows — scalable server architectures", color: "#fff4f1", border: "#fdd0c2" },
  { icon: "📱", title: "Mobile Dev", desc: "React Native, Expo, iOS & Android deployment, Redux / Context API", color: "#eef4ff", border: "#c2d8f8" },
  { icon: "🗄️", title: "Database", desc: "MongoDB, MySQL — schema design, aggregation, indexing & optimization", color: "#fff4f1", border: "#fdd0c2" },
];

const expItems = [
  "Teach React.js, Node.js, Express.js, MongoDB & React Native to undergraduate and postgraduate students.",
  "Develop structured curriculum covering front-end, backend APIs, database design, and mobile app development.",
  "Conduct workshops on Git/GitHub, REST API development, and MongoDB best practices.",
  "Guide students through real-world projects — responsive UIs, scalable backends, and high-performance mobile apps.",
  "Evaluate progress, conduct code reviews, and provide personalized mentoring.",
  "Mentor students on full-stack capstone projects: API integration, DB modeling, cloud deployment & optimization.",
];

const certs = [
  { icon: "⚡", name: "IT Specialist – JavaScript", org: "Certiport", year: "2023" },
  { icon: "🎨", name: "IT Specialist – HTML & CSS", org: "Certiport", year: "2023" },
];

/* ─── SECTION HEADER ─── */
function SectionHead({ title, sub }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#eef4ff", border: "1px solid #c2d8f8", borderRadius: 99, padding: "5px 16px", fontSize: 11, color: "#0057B8", fontWeight: 700, letterSpacing: 1.8, textTransform: "uppercase", marginBottom: 12 }}>
        <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#E84A1A" }} />
        {sub}
      </div>
      <h2 style={{ fontSize: "clamp(26px,4vw,46px)", fontWeight: 900, color: "#003B7E", lineHeight: 1.1 }}>{title}</h2>
      <div style={{ display: "flex", gap: 7, marginTop: 12, alignItems: "center" }}>
        <div style={{ width: 44, height: 4, background: "#E84A1A", borderRadius: 2 }} />
        <div style={{ width: 16, height: 4, background: "#0057B8", borderRadius: 2 }} />
        <div style={{ width: 7, height: 4, background: "#dce8f8", borderRadius: 2 }} />
      </div>
    </div>
  );
}

/* ─── MAIN ─── */
export default function Portfolio() {
  const [navSolid, setNavSolid] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const onScroll = () => setNavSolid(window.scrollY > 60);
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("scroll", onScroll);
    window.addEventListener("resize", onResize);
    onResize();
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onResize); };
  }, []);

  const scrollTo = (id) => { document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); setMenuOpen(false); };

  const px = isMobile ? "20px" : "clamp(20px, 5vw, 72px)";

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');
    *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
    html { scroll-behavior: smooth; }
    body { background:#f5f8fe; font-family:'Poppins',sans-serif; overflow-x:hidden; }

    @keyframes blink    { 0%,100%{opacity:1} 50%{opacity:0} }
    @keyframes floatP   { 0%{transform:translateY(0) scale(1)} 100%{transform:translateY(-40px) scale(1.3)} }
    @keyframes spin     { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
    @keyframes spinRev  { from{transform:rotate(360deg)} to{transform:rotate(0deg)} }
    @keyframes ripple   { 0%{transform:scale(1);opacity:.55} 100%{transform:scale(2.4);opacity:0} }
    @keyframes pulse    { 0%,100%{box-shadow:0 0 0 0 rgba(76,175,80,.5)} 50%{box-shadow:0 0 0 12px rgba(76,175,80,0)} }
    @keyframes morphBg  { 0%,100%{border-radius:60% 40% 60% 40%/50% 60% 40% 50%} 50%{border-radius:40% 60% 40% 60%/60% 40% 60% 40%} }
    @keyframes badgeBob { 0%,100%{transform:translateY(0) rotate(-4deg)} 50%{transform:translateY(-8px) rotate(-4deg)} }
    @keyframes waveLine { 0%,100%{transform:scaleX(1)} 50%{transform:scaleX(1.2)} }
    @keyframes heroL    { from{opacity:0;transform:translateX(-70px)} to{opacity:1;transform:translateX(0)} }
    @keyframes heroR    { from{opacity:0;transform:translateX(70px)} to{opacity:1;transform:translateX(0)} }
    @keyframes heroU    { from{opacity:0;transform:translateY(32px)} to{opacity:1;transform:translateY(0)} }
    @keyframes slideDown { from{opacity:0;transform:translateY(-12px)} to{opacity:1;transform:translateY(0)} }

    .ht1 { animation: heroL .9s cubic-bezier(.16,1,.3,1) .15s both; }
    .ht2 { animation: heroL .9s cubic-bezier(.16,1,.3,1) .3s both; }
    .ht3 { animation: heroU .9s cubic-bezier(.16,1,.3,1) .45s both; }
    .ht4 { animation: heroU .9s cubic-bezier(.16,1,.3,1) .6s both; }
    .ht5 { animation: heroU .9s cubic-bezier(.16,1,.3,1) .75s both; }
    .hi  { animation: heroR .9s cubic-bezier(.16,1,.3,1) .25s both; }

    .nav-pill:hover    { background:rgba(0,87,184,.08)!important; color:#0057B8!important; }
    .comp-card         { transition: transform .35s cubic-bezier(.16,1,.3,1), box-shadow .35s ease !important; }
    .comp-card:hover   { transform:translateY(-10px) scale(1.02)!important; box-shadow:0 24px 48px rgba(0,87,184,.14)!important; }
    .cert-card         { transition: transform .35s cubic-bezier(.16,1,.3,1), box-shadow .35s ease !important; }
    .cert-card:hover   { transform:translateY(-8px)!important; box-shadow:0 20px 44px rgba(232,74,26,.16)!important; }
    .btn-p             { transition: all .25s cubic-bezier(.16,1,.3,1) !important; }
    .btn-p:hover       { transform:translateY(-3px) scale(1.03)!important; box-shadow:0 16px 36px rgba(232,74,26,.45)!important; }
    .btn-o             { transition: all .25s cubic-bezier(.16,1,.3,1) !important; }
    .btn-o:hover       { background:rgba(255,255,255,.18)!important; transform:translateY(-3px)!important; }
    .exp-item:hover    { background:#f0f6ff!important; border-left-color:#E84A1A!important; }
    .tag               { transition: all .2s; }
    .tag:hover         { background:#0057B8!important; color:#fff!important; transform:scale(1.06); }
    .mobile-menu       { animation: slideDown .3s cubic-bezier(.16,1,.3,1) both; }
    .hamburger-line    { transition: all .3s cubic-bezier(.16,1,.3,1); }

    ::-webkit-scrollbar       { width: 5px; }
    ::-webkit-scrollbar-track { background: #dce8f8; }
    ::-webkit-scrollbar-thumb { background: linear-gradient(#0057B8,#E84A1A); border-radius: 4px; }

    @media (max-width: 767px) {
      .hero-grid   { flex-direction: column-reverse !important; align-items: center !important; text-align: center !important; }
      .hero-left   { max-width: 100% !important; }
      .hero-stats  { justify-content: center !important; }
      .hero-btns   { justify-content: center !important; }
      .about-grid  { grid-template-columns: 1fr !important; }
      .skills-grid { grid-template-columns: 1fr !important; }
      .exp-header  { flex-direction: column !important; align-items: flex-start !important; }
      .exp-badge   { align-self: flex-start !important; }
    }

    @media (min-width: 768px) and (max-width: 1024px) {
      .about-grid  { grid-template-columns: 1fr !important; }
      .skills-grid { grid-template-columns: 1fr !important; }
      .hero-grid   { gap: 32px !important; }
    }
  `;

  const photoSize = isMobile ? 200 : 300;

  return (
    <>
      <style>{css}</style>
      <div style={{ fontFamily: "'Poppins',sans-serif", background: "#f5f8fe", color: "#1e3a5f" }}>

        {/* ══ NAV ══ */}
        <nav style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 999,
          background: navSolid ? "rgba(255,255,255,0.97)" : "transparent",
          backdropFilter: navSolid ? "blur(20px)" : "none",
          borderBottom: navSolid ? "1px solid rgba(0,87,184,.1)" : "none",
          boxShadow: navSolid ? "0 4px 28px rgba(0,87,184,.08)" : "none",
          transition: "all .4s ease",
          padding: `0 ${px}`, height: 64,
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: "100%", maxWidth: 1160, margin: "0 auto" }}>
            {/* LOGO */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => scrollTo("hero")}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: "linear-gradient(135deg,#003B7E,#0057B8)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 14px rgba(0,87,184,.35)", flexShrink: 0 }}>
                <span style={{ color: "#fff", fontWeight: 900, fontSize: 17 }}>S</span>
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: navSolid ? "#003B7E" : "#fff", lineHeight: 1.1, transition: "color .3s" }}>Sathish</div>
                <div style={{ fontSize: 10, fontWeight: 500, color: navSolid ? "#7a9cc2" : "rgba(255,255,255,.6)", letterSpacing: 1, transition: "color .3s" }}>LUTUKURTHI</div>
              </div>
            </div>

            {/* DESKTOP NAV */}
            {!isMobile && (
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                {["about","skills","experience","certifications"].map(s => (
                  <button key={s} className="nav-pill" onClick={() => scrollTo(s)} style={{ background: "none", border: "none", cursor: "pointer", color: navSolid ? "#4a6a9e" : "rgba(255,255,255,.8)", fontSize: 13, fontWeight: 600, padding: "7px 16px", borderRadius: 8, textTransform: "capitalize", letterSpacing: .3, transition: "all .2s" }}>{s}</button>
                ))}
                <button className="btn-p" onClick={() => scrollTo("experience")} style={{ background: "linear-gradient(135deg,#E84A1A,#ff6b40)", color: "#fff", border: "none", padding: "9px 22px", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", marginLeft: 10, boxShadow: "0 6px 20px rgba(232,74,26,.38)" }}>Hire Me →</button>
              </div>
            )}

            {/* HAMBURGER */}
            {isMobile && (
              <button onClick={() => setMenuOpen(o => !o)} style={{ background: "none", border: "none", cursor: "pointer", padding: 8, display: "flex", flexDirection: "column", gap: 5, justifyContent: "center" }}>
                <div className="hamburger-line" style={{ width: 24, height: 2, background: navSolid ? "#003B7E" : "#fff", borderRadius: 2, transform: menuOpen ? "rotate(45deg) translate(5px, 5px)" : "none" }} />
                <div className="hamburger-line" style={{ width: 24, height: 2, background: navSolid ? "#003B7E" : "#fff", borderRadius: 2, opacity: menuOpen ? 0 : 1 }} />
                <div className="hamburger-line" style={{ width: 24, height: 2, background: navSolid ? "#003B7E" : "#fff", borderRadius: 2, transform: menuOpen ? "rotate(-45deg) translate(5px, -5px)" : "none" }} />
              </button>
            )}
          </div>

          {/* MOBILE DROPDOWN MENU */}
          {isMobile && menuOpen && (
            <div className="mobile-menu" style={{ background: "rgba(255,255,255,0.98)", backdropFilter: "blur(20px)", borderTop: "1px solid rgba(0,87,184,.1)", padding: "12px 20px 20px", boxShadow: "0 12px 40px rgba(0,87,184,.12)" }}>
              {["about","skills","experience","certifications"].map(s => (
                <button key={s} onClick={() => scrollTo(s)} style={{ display: "block", width: "100%", background: "none", border: "none", cursor: "pointer", color: "#4a6a9e", fontSize: 15, fontWeight: 600, padding: "12px 0", textAlign: "left", textTransform: "capitalize", borderBottom: "1px solid rgba(0,87,184,.06)" }}>{s}</button>
              ))}
              <button className="btn-p" onClick={() => scrollTo("experience")} style={{ background: "linear-gradient(135deg,#E84A1A,#ff6b40)", color: "#fff", border: "none", padding: "12px 0", borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: "pointer", width: "100%", marginTop: 12, boxShadow: "0 6px 20px rgba(232,74,26,.38)" }}>Hire Me →</button>
            </div>
          )}
        </nav>

        {/* ══ HERO ══ */}
        <section id="hero" style={{ minHeight: "100vh", display: "flex", alignItems: "center", background: "linear-gradient(145deg, rgb(197,222,255) 0%, rgb(88,137,193) 45%, rgb(13,50,87) 100%)", padding: `100px ${px} 72px`, position: "relative", overflow: "hidden" }}>
          <Particles />
          <div style={{ position: "absolute", top: "50%", right: "5%", transform: "translateY(-50%)", width: 520, height: 520, borderRadius: "50%", border: "1.5px solid rgba(255,255,255,.05)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", top: "50%", right: "5%", transform: "translateY(-50%)", width: 370, height: 370, borderRadius: "50%", border: "1.5px solid rgba(255,255,255,.08)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", bottom: "-12%", left: "-6%", width: 360, height: 360, borderRadius: "50%", background: "rgba(232,74,26,.12)", animation: "morphBg 8s ease-in-out infinite", pointerEvents: "none" }} />

          <div style={{ maxWidth: 1160, margin: "0 auto", width: "100%" }}>
            <div className="hero-grid" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: isMobile ? 32 : 48 }}>

              {/* LEFT */}
              <div className="hero-left" style={{ flex: 1, maxWidth: 600, minWidth: 0 }}>
                <div className="ht1" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,.12)", border: "1px solid rgba(255,255,255,.22)", borderRadius: 99, padding: "6px 18px", fontSize: 12, color: "#fff", fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 24 }}>
                  <span style={{ fontSize: 16 }}>👋</span> Hello, I'm
                </div>
                <h1 className="ht2" style={{ fontSize: "clamp(32px,5.5vw,68px)", fontWeight: 900, color: "#fff", lineHeight: 1.05, marginBottom: 4 }}>Sathish</h1>
                <h1 className="ht2" style={{ fontSize: "clamp(32px,5.5vw,68px)", fontWeight: 900, lineHeight: 1.05, marginBottom: 22, background: "linear-gradient(90deg,#E84A1A,#ff8c5a)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>Lutukurthi</h1>
                <div className="ht3" style={{ fontSize: isMobile ? 16 : 20, marginBottom: 22, minHeight: 34, fontWeight: 500, color: "rgba(255,255,255,.8)" }}>
                  I'm a &nbsp;<TypeWriter words={["Full Stack Developer","PHP Developer","Node.js Expert","MySQL Specialist","React Native Dev"]} />
                </div>
                <p className="ht4" style={{ fontSize: isMobile ? 14 : 15, lineHeight: 1.9, color: "rgba(255,255,255,.62)", maxWidth: 500, marginBottom: 38, fontWeight: 400 }}>
                  Building scalable web & mobile applications with React.js, Node.js, React Native, and MongoDB. Certified developer & passionate educator at <span style={{ color: "#E84A1A", fontWeight: 600 }}>Aditya University</span>.
                </p>
                <div className="ht5 hero-btns" style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 52 }}>
                  <button className="btn-p" onClick={() => scrollTo("experience")} style={{ background: "linear-gradient(135deg,#E84A1A,#ff6033)", color: "#fff", border: "none", padding: isMobile ? "12px 24px" : "15px 34px", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer", boxShadow: "0 10px 32px rgba(232,74,26,.45)", letterSpacing: .3 }}>View Experience</button>
                  <button className="btn-o" onClick={() => scrollTo("about")} style={{ background: "rgba(255,255,255,.1)", color: "#fff", border: "1.5px solid rgba(255,255,255,.3)", padding: isMobile ? "12px 24px" : "15px 34px", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>About Me</button>
                </div>
                <div className="ht5 hero-stats" style={{ display: "flex", gap: 0 }}>
                  {[["250+","Students Trained"],["2","Certifications"],["6+","Yrs Experience"]].map(([n, l], i) => (
                    <div key={l} style={{ paddingRight: isMobile ? 20 : 36, borderRight: i < 2 ? "1px solid rgba(255,255,255,.15)" : "none", marginRight: i < 2 ? (isMobile ? 20 : 36) : 0 }}>
                      <div style={{ fontSize: isMobile ? 24 : 32, fontWeight: 900, color: "#fff", lineHeight: 1 }}>{n}</div>
                      <div style={{ fontSize: isMobile ? 9 : 11, color: "rgba(255,255,255,.45)", fontWeight: 500, letterSpacing: 1, textTransform: "uppercase", marginTop: 5 }}>{l}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* RIGHT — Photo */}
              <div className="hi" style={{ position: "relative", flexShrink: 0, width: photoSize, height: photoSize }}>
                <div style={{ position: "absolute", inset: -18, borderRadius: "50%", border: "2px dashed rgba(232,74,26,.4)", animation: "spin 14s linear infinite", pointerEvents: "none" }} />
                <div style={{ position: "absolute", inset: -8, borderRadius: "50%", border: "1.5px solid rgba(255,255,255,.12)", animation: "spinRev 10s linear infinite", pointerEvents: "none" }} />
                <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "2px solid rgba(232,74,26,.5)", animation: "ripple 2.5s ease-out infinite", pointerEvents: "none" }} />
                <div style={{ position: "relative", width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden", border: "4px solid rgba(255,255,255,.18)", boxShadow: "0 24px 60px rgba(0,0,0,.35),0 0 0 8px rgba(255,255,255,.06)" }}>
                  <img src="https://adityauniversity.in/adtppu/SDG/4294.png" alt="Sathish Lutukurthi" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }} />
                </div>
                {!isMobile && (
                  <div style={{ position: "absolute", bottom: 10, right: -18, zIndex: 3, background: "linear-gradient(135deg,#E84A1A,#ff6033)", color: "#fff", borderRadius: 12, padding: "10px 16px", fontSize: 11, fontWeight: 700, letterSpacing: .5, boxShadow: "0 10px 28px rgba(232,74,26,.5)", animation: "badgeBob 3s ease-in-out infinite", whiteSpace: "nowrap" }}>⚡ Full Stack Developer</div>
                )}
                {!isMobile && (
                  <div style={{ position: "absolute", top: -10, left: -20, display: "grid", gridTemplateColumns: "repeat(4,8px)", gap: 6, opacity: .35 }}>
                    {Array.from({ length: 16 }).map((_, i) => (
                      <div key={i} style={{ width: 5, height: 5, borderRadius: "50%", background: i % 2 === 0 ? "#E84A1A" : "#fff" }} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{ position: "absolute", bottom: 32, left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, opacity: .45, cursor: "pointer" }} onClick={() => scrollTo("about")}>
            <span style={{ fontSize: 11, color: "#fff", letterSpacing: 2, textTransform: "uppercase", fontWeight: 500 }}>Scroll</span>
            <div style={{ width: 1.5, height: 36, background: "linear-gradient(to bottom,rgba(255,255,255,.7),transparent)", borderRadius: 2 }} />
          </div>
        </section>

        {/* ══ ABOUT ══ */}
        <section id="about" style={{ padding: `96px ${px}`, background: "#fff" }}>
          <div style={{ maxWidth: 1160, margin: "0 auto" }}>
            <Reveal><SectionHead title="About Me" sub="Who I am" /></Reveal>
            <div className="about-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, marginTop: 52, alignItems: "start" }}>
              <Reveal delay={0.1} dir="left">
                <p style={{ fontSize: 15.5, lineHeight: 2, color: "#4a6a9e", fontWeight: 400 }}>
                  I am a proficient <strong style={{ color: "#0057B8" }}>Full Stack Developer</strong> with hands-on experience building scalable web and mobile applications using React.js, Node.js, React Native, and MongoDB. I specialize in crafting clean, efficient, and reliable solutions with a strong focus on performance and user experience.
                </p>
                <p style={{ fontSize: 15.5, lineHeight: 2, color: "#4a6a9e", fontWeight: 400, marginTop: 18 }}>
                  I hold certifications as an <strong style={{ color: "#E84A1A" }}>IT Specialist in HTML & CSS</strong> and <strong style={{ color: "#E84A1A" }}>IT Specialist in JavaScript</strong> from Certiport.
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 30 }}>
                  {["React.js","Node.js","MongoDB","React Native","PHP","MySQL","Express.js","REST APIs"].map(t => (
                    <span key={t} className="tag" style={{ background: "#eef4ff", color: "#0057B8", border: "1px solid #c2d8f8", borderRadius: 7, padding: "5px 13px", fontSize: 12, fontWeight: 600, cursor: "default" }}>{t}</span>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.2} dir="right">
                <div style={{ background: "linear-gradient(145deg,#003B7E,#0068d6)", borderRadius: 22, padding: isMobile ? 24 : 38, position: "relative", overflow: "hidden", boxShadow: "0 20px 50px rgba(0,87,184,.22)" }}>
                  <div style={{ position: "absolute", top: -40, right: -40, width: 150, height: 150, borderRadius: "50%", background: "rgba(232,74,26,.18)" }} />
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)", fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 18 }}>Current Position</div>
                  <div style={{ fontSize: isMobile ? 18 : 24, fontWeight: 800, color: "#fff", marginBottom: 6 }}>Full Stack Developer</div>
                  <div style={{ fontSize: 14, color: "#E84A1A", marginBottom: 5, fontWeight: 600 }}>Full Stack Development Dept.</div>
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,.5)", marginBottom: 28, fontWeight: 400 }}>Aditya University · Kakinada, AP</div>
                  <div style={{ display: "flex", gap: 24, marginBottom: 24 }}>
                    {[["250+","Students"],["6+","Years"],["2","Certs"]].map(([n, l]) => (
                      <div key={l} style={{ textAlign: "center" }}>
                        <div style={{ fontSize: isMobile ? 20 : 26, fontWeight: 900, color: "#fff" }}>{n}</div>
                        <div style={{ fontSize: 10, color: "rgba(255,255,255,.45)", fontWeight: 500, letterSpacing: 1, textTransform: "uppercase" }}>{l}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,.1)", border: "1px solid rgba(255,255,255,.18)", borderRadius: 99, padding: "8px 16px" }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#4caf50", animation: "pulse 2s infinite", flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: "rgba(255,255,255,.75)", fontWeight: 500 }}>March 2019 – Present</span>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ══ COMPETENCIES ══ */}
        <section style={{ padding: `96px ${px}`, background: "#f5f8fe" }}>
          <div style={{ maxWidth: 1160, margin: "0 auto" }}>
            <Reveal><SectionHead title="Core Competencies" sub="What I do best" /></Reveal>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 24, marginTop: 52 }}>
              {competencies.map((c, i) => (
                <Reveal key={i} delay={i * 0.1}>
                  <div className="comp-card" style={{ background: "#fff", borderRadius: 18, padding: "32px 26px", border: "1.5px solid #dce8f8", cursor: "default", boxShadow: "0 4px 18px rgba(0,87,184,.06)" }}>
                    <div style={{ width: 54, height: 54, borderRadius: 14, background: c.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 18, border: `1.5px solid ${c.border}` }}>{c.icon}</div>
                    <h5 style={{ color: "#003B7E", fontSize: 15, fontWeight: 800, marginBottom: 10 }}>{c.title}</h5>
                    <p style={{ fontSize: 13, color: "#7a9cc2", lineHeight: 1.75, fontWeight: 400 }}>{c.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ══ SKILLS ══ */}
        <section id="skills" style={{ padding: `96px ${px}`, background: "#fff" }}>
          <div style={{ maxWidth: 1160, margin: "0 auto" }}>
            <Reveal><SectionHead title="Technical Skills" sub="My tech stack" /></Reveal>
            <div className="skills-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 70, marginTop: 52 }}>
              <div>{skills.map((s, i) => <SkillBar key={i} label={s.label} pct={s.pct} delay={i * 0.12} />)}</div>
              <Reveal delay={0.2}>
                <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
                  {[
                    ["⚛️","Front-End","React.js, JS ES6+, HTML5, CSS3, Tailwind, Bootstrap"],
                    ["🔧","Back-End","Node.js, Express.js, REST APIs, Auth & Authorization"],
                    ["📱","Mobile","React Native, Expo, Android & iOS, Redux, Context API"],
                    ["🗄️","Database","MongoDB, Mongoose, Schema Design, Aggregation, MySQL"],
                    ["☁️","DevOps","AWS EC2/S3, Docker, GitHub Actions, Nginx, PM2"],
                  ].map(([icon, cat, techs], i) => (
                    <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "15px 18px", background: "#f5f8fe", borderRadius: 11, border: "1px solid #dce8f8" }}>
                      <span style={{ fontSize: 18 }}>{icon}</span>
                      <div>
                        <div style={{ color: "#0057B8", fontWeight: 700, fontSize: 12, marginBottom: 3 }}>{cat}</div>
                        <div style={{ fontSize: 12.5, color: "#7a9cc2", lineHeight: 1.6, fontWeight: 400 }}>{techs}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ══ EXPERIENCE ══ */}
        <section id="experience" style={{ padding: `96px ${px}`, background: "#f5f8fe" }}>
          <div style={{ maxWidth: 1160, margin: "0 auto" }}>
            <Reveal><SectionHead title="Experience" sub="Professional journey" /></Reveal>
            <Reveal delay={0.1}>
              <div style={{ background: "#fff", borderRadius: 22, padding: isMobile ? 24 : 48, border: "1.5px solid #dce8f8", marginTop: 52, position: "relative", overflow: "hidden", boxShadow: "0 8px 36px rgba(0,87,184,.08)" }}>
                <div style={{ position: "absolute", top: 0, left: 0, width: 5, height: "100%", background: "linear-gradient(to bottom,#E84A1A,#0057B8)" }} />
                <div className="exp-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 18, marginBottom: 38 }}>
                  <div>
                    <h3 style={{ color: "#003B7E", fontSize: isMobile ? 18 : 22, fontWeight: 800, marginBottom: 5 }}>Full Stack Developer</h3>
                    <div style={{ color: "#E84A1A", fontSize: 14, fontWeight: 700 }}>Aditya University · Kakinada, Andhra Pradesh</div>
                  </div>
                  <div className="exp-badge" style={{ background: "linear-gradient(135deg,#003B7E,#0068d6)", borderRadius: 10, padding: "10px 20px", fontSize: 13, color: "#fff", fontWeight: 700, boxShadow: "0 6px 18px rgba(0,87,184,.3)" }}>March 2026 – Present</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  {expItems.map((item, i) => (
                    <div key={i} className="exp-item" style={{ display: "flex", gap: 16, alignItems: "flex-start", padding: "14px 14px 14px 0", borderLeft: "2.5px solid #dce8f8", marginLeft: 8, transition: "border-color .2s, background .2s", borderRadius: "0 8px 8px 0" }}>
                      <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", border: "2.5px solid #0057B8", flexShrink: 0, marginTop: 3, marginLeft: -9, boxShadow: "0 0 0 4px #f5f8fe" }} />
                      <p style={{ fontSize: isMobile ? 13 : 14, color: "#4a6a9e", lineHeight: 1.8, margin: 0, fontWeight: 400, paddingLeft: 6 }}>{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div style={{ marginTop: 48 }}>
                <h3 style={{ color: "#003B7E", fontSize: 18, fontWeight: 800, marginBottom: 28, paddingLeft: 18, borderLeft: "4px solid #E84A1A" }}>Key Achievements</h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 20 }}>
                  {[
                    ["🎓","2500+","Students trained & mentored in full stack development"],
                    ["📚","Curriculum","React.js, Node.js, MongoDB, Express.js & React Native"],
                    ["🚀","Projects","Real-world API, DB modeling & cloud deployment"],
                    ["⭐","Top Rated","Recognized for high-quality, impactful instruction"],
                  ].map(([icon, title, desc], i) => (
                    <div key={i} style={{ background: "#fff", borderRadius: 16, padding: "24px 22px", border: "1.5px solid #dce8f8", display: "flex", gap: 16, alignItems: "flex-start", boxShadow: "0 4px 16px rgba(0,87,184,.06)" }}>
                      <div style={{ width: 46, height: 46, borderRadius: 12, background: "linear-gradient(135deg,#eef4ff,#dce8f8)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>{icon}</div>
                      <div>
                        <div style={{ color: "#0057B8", fontWeight: 800, fontSize: 14, marginBottom: 5 }}>{title}</div>
                        <div style={{ color: "#7a9cc2", fontSize: 12.5, lineHeight: 1.65, fontWeight: 400 }}>{desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ══ CERTIFICATIONS ══ */}
        <section id="certifications" style={{ padding: `96px ${px}`, background: "#fff" }}>
          <div style={{ maxWidth: 1160, margin: "0 auto" }}>
            <Reveal><SectionHead title="Certifications" sub="Proof of expertise" /></Reveal>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 26, marginTop: 52 }}>
              {certs.map((c, i) => (
                <Reveal key={i} delay={i * 0.15}>
                  <div className="cert-card" style={{ background: "#f5f8fe", borderRadius: 20, padding: "38px 30px", border: "1.5px solid #dce8f8", cursor: "default" }}>
                    <div style={{ width: 64, height: 64, borderRadius: 16, background: "linear-gradient(135deg,#003B7E,#0068d6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30, marginBottom: 20, boxShadow: "0 8px 22px rgba(0,87,184,.25)" }}>{c.icon}</div>
                    <div style={{ color: "#003B7E", fontWeight: 800, fontSize: 17, marginBottom: 6 }}>{c.name}</div>
                    <div style={{ color: "#E84A1A", fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{c.org}</div>
                    <div style={{ color: "#7a9cc2", fontSize: 12, marginBottom: 20, fontWeight: 500 }}>{c.year}</div>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#eef4ff", border: "1px solid #c2d8f8", borderRadius: 99, padding: "7px 15px" }}>
                      <span style={{ fontSize: 14 }}>✅</span>
                      <span style={{ fontSize: 12, color: "#0057B8", fontWeight: 700 }}>Verified Certified</span>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ══ FOOTER ══ */}
        <footer style={{ padding: `52px ${px}`, background: "linear-gradient(145deg,#002D6B,#004BA0)", textAlign: "center" }}>
          <div style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: "#fff", marginBottom: 8, letterSpacing: .5 }}>Sathish Lutukurthi</div>
          <p style={{ color: "rgba(255,255,255,.45)", fontSize: 13, marginBottom: 28, fontWeight: 400 }}>Full Stack Developer · Aditya University, Kakinada</p>
          <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 8, marginBottom: 28 }}>
            {["React.js","Node.js","MongoDB","PHP","MySQL"].map(t => (
              <span key={t} style={{ background: "rgba(255,255,255,.08)", color: "rgba(255,255,255,.6)", borderRadius: 6, padding: "4px 12px", fontSize: 11, fontWeight: 600, border: "1px solid rgba(255,255,255,.12)" }}>{t}</span>
            ))}
          </div>
          <div style={{ width: 40, height: 3, background: "#E84A1A", borderRadius: 2, margin: "0 auto", animation: "waveLine 3s ease-in-out infinite" }} />
          <p style={{ color: "rgba(255,255,255,.25)", fontSize: 11, marginTop: 24, fontWeight: 400, letterSpacing: 1 }}>© 2026 SATHISH LUTUKURTHI · ALL RIGHTS RESERVED</p>
        </footer>

      </div>
    </>
  );
}